import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  Typography,
  Stack,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopyOutlined,
  DoneOutlined,
  IntegrationInstructionsOutlined,
  RestaurantOutlined,
  SmartToyOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { createRestaurante, updateRestaurante } from '../../services/api';

const emptyRest = {
  nome: '',
  telefone: '',
  instance_id: '',
  instance_token: '',
  lat: '',
  long: '',
  raio: '',
  whatsapp_access_token: '',
  whatsapp_phone_number_id: '',
  is_ativo: true,
  is_food: false,
  code: '',
  gmn_link: '',
  intervalo_checkin: '',
  checkin_slug: '',
  ultimo_checkin_primeira_mensagem: '',
  ultimo_checkin_mensagem_recorrente: '',
  ultimo_checkin_recorrencia: '',
  ultimo_checkin_brinde_id: '',
  checkin_final: '',
  ultimo_checkin_nome: '',
  atendabot_assistant: '',
  atendabot_active: false,
  pedidos_habilitados: false,
  atendabot_phone: '',
  atendabot_instance_id: '',
  atendabot_instance_token: '',
  atendabot_transbordo_frase: '',
  atendabot_transbordo_phone: '',
  usar_cardapio_onemenu: false,
  atendabot_promotions: false,
  atendabot_ai: '',
  gemini_key: '',
  gemini_prompt: '',
  generated_prompt: '',
  atendabot_usar_prompt_manager: false,
  gemini_prompt_fechado: '',
  delay: '',
  primeira_mensagem: '',
  delay_primeira_mensagem: '',
  enviar_status: false,
  tempo_resposta: '',
  retentativas: '',
  integration_sischef_active: false,
  integration_sischef_token: '',
};

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const makeIconBox = (color, bg, border) => ({
  width: 36,
  height: 36,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bg,
  color,
  flexShrink: 0,
  border: `1px solid ${border}`,
});

const ICON_INDIGO = makeIconBox(
  '#6366f1',
  'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  'rgba(99,102,241,0.18)',
);
const ICON_CYAN = makeIconBox(
  '#0891b2',
  'linear-gradient(135deg, rgba(8,145,178,0.14) 0%, rgba(6,182,212,0.10) 100%)',
  'rgba(8,145,178,0.18)',
);
const ICON_VIOLET = makeIconBox(
  '#7c3aed',
  'linear-gradient(135deg, rgba(124,58,237,0.14) 0%, rgba(139,92,246,0.10) 100%)',
  'rgba(124,58,237,0.18)',
);
const ICON_SLATE = makeIconBox(
  '#475569',
  'rgba(15,23,42,0.06)',
  'rgba(15,23,42,0.10)',
);

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

const SectionCard = ({ title, description, icon, iconSx, children }) => (
  <Box sx={CARD_SX}>
    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
        <Box sx={iconSx}>{icon}</Box>
        <Box>
          <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  </Box>
);

const RestauranteFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = id && id !== 'new';
  const [form, setForm] = useState(location.state || emptyRest);
  const [promptCopied, setPromptCopied] = useState(false);

  const handleChange = (name) => (e) => setForm({ ...form, [name]: e.target.value });
  const handleCheck  = (name) => (e) => setForm({ ...form, [name]: e.target.checked });

  const handleCopyGeneratedPrompt = async () => {
    if (!form.generated_prompt) return;
    try {
      await navigator.clipboard.writeText(form.generated_prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar o prompt gerado:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // generated_prompt é apenas leitura aqui (gerado pelo Atendabot) e não deve ser reenviado.
    const { generated_prompt, ...formToSubmit } = form;
    const payload = { ...formToSubmit, telefone: form.telefone || null, atendabot_phone: form.atendabot_phone || null };
    if (editing) await updateRestaurante(id, payload);
    else await createRestaurante(payload);
    navigate(-1);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Box component="form" onSubmit={handleSubmit} autoComplete="off">

          {/* ── Buttons row (outside cards) ── */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#475569', fontWeight: 600, borderRadius: '12px', px: 1.5, py: 0.75,
                '&:hover': { background: 'rgba(99,102,241,0.08)', color: '#6366f1' },
              }}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: '12px', px: 3, py: 1, fontWeight: 700, boxShadow: 'none',
                bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                transition: 'all 150ms ease',
              }}
            >
              Salvar
            </Button>
          </Stack>

          {/* ── Page title card ── */}
          <Box sx={{ ...CARD_SX, mb: 2.5 }}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 48, height: 48, borderRadius: '14px' }}>
                  <StorefrontOutlined sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                    {editing ? 'Editar Estabelecimento' : 'Novo Estabelecimento'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Preencha cada seção com as informações do estabelecimento.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Stack spacing={2.5}>

            {/* Dados Principais */}
            <SectionCard
              title="Dados Principais"
              description="Informações básicas do estabelecimento."
              icon={<StorefrontOutlined sx={{ fontSize: 18 }} />}
              iconSx={ICON_INDIGO}
            >
              <Grid item xs={12} sm={4}>
                <TextField label="Nome" fullWidth required value={form.nome} onChange={handleChange('nome')} sx={fieldSx} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControlLabel control={<Checkbox checked={!!form.is_ativo} onChange={handleCheck('is_ativo')} />} label="Restaurante Ativo" />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControlLabel control={<Checkbox checked={!!form.is_food} onChange={handleCheck('is_food')} />} label="É Food" />
              </Grid>
            </SectionCard>

            {/* Clube de Mesa */}
            <SectionCard
              title="Clube de Mesa"
              description="Configurações do programa de fidelidade."
              icon={<RestaurantOutlined sx={{ fontSize: 18 }} />}
              iconSx={ICON_CYAN}
            >
              <Grid item xs={12} sm={6}><TextField label="Telefone CDM (Sem o 9)" fullWidth value={form.telefone} onChange={handleChange('telefone')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Z-API Instance ID" fullWidth value={form.instance_id} onChange={handleChange('instance_id')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Z-API Instance Token" fullWidth value={form.instance_token} onChange={handleChange('instance_token')} sx={fieldSx} /></Grid>
              <Grid item xs={6}><TextField label="Check-in Slug" fullWidth value={form.checkin_slug} onChange={handleChange('checkin_slug')} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Latitude" type="number" fullWidth value={form.lat} onChange={handleChange('lat')} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Longitude" type="number" fullWidth value={form.long} onChange={handleChange('long')} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Raio (metros)" type="number" fullWidth value={form.raio} onChange={handleChange('raio')} sx={fieldSx} /></Grid>
              <Grid item xs={6}><TextField label="Intervalo Check-in (min)" type="number" fullWidth value={form.intervalo_checkin} onChange={handleChange('intervalo_checkin')} sx={fieldSx} /></Grid>
              <Grid item xs={12}><TextField label="Nome Último Nível" fullWidth value={form.ultimo_checkin_nome} onChange={handleChange('ultimo_checkin_nome')} sx={fieldSx} /></Grid>
              <Grid item xs={4}><TextField label="Check-in Final" type="number" fullWidth value={form.checkin_final} onChange={handleChange('checkin_final')} sx={fieldSx} /></Grid>
              <Grid item xs={12}><TextField label="Mensagem Último Nível" fullWidth value={form.ultimo_checkin_primeira_mensagem} onChange={handleChange('ultimo_checkin_primeira_mensagem')} sx={fieldSx} /></Grid>
              <Grid item xs={12}><TextField label="Mensagem Recorrente Último Nível" fullWidth value={form.ultimo_checkin_mensagem_recorrente} onChange={handleChange('ultimo_checkin_mensagem_recorrente')} sx={fieldSx} /></Grid>
              <Grid item xs={4}><TextField label="Recorrência de Qtos Check-ins" type="number" fullWidth value={form.ultimo_checkin_recorrencia} onChange={handleChange('ultimo_checkin_recorrencia')} sx={fieldSx} /></Grid>
              <Grid item xs={4}><TextField label="Brinde ID Recorrência" fullWidth value={form.ultimo_checkin_brinde_id} onChange={handleChange('ultimo_checkin_brinde_id')} sx={fieldSx} /></Grid>
            </SectionCard>

            {/* Atendabot */}
            <SectionCard
              title="Atendabot"
              description="Configurações do assistente virtual com IA."
              icon={<SmartToyOutlined sx={{ fontSize: 18 }} />}
              iconSx={ICON_VIOLET}
            >
              <Grid item xs={12} sm={6}><FormControlLabel control={<Checkbox checked={!!form.atendabot_active} onChange={handleCheck('atendabot_active')} />} label="Atendabot Ativo" /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!form.pedidos_habilitados} onChange={handleCheck('pedidos_habilitados')} />}
                  label="Controle de pedidos pela IA"
                />
                <Typography variant="body2" sx={{ color: '#64748b', ml: 4, mt: -0.5 }}>
                  Quando desativado, a IA pode orientar o cliente pelo link definido no prompt, mas não monta, registra, acompanha ou cancela pedidos para esta unidade.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}><TextField label="Telefone Atendabot (Sem o 9)" fullWidth value={form.atendabot_phone} onChange={handleChange('atendabot_phone')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Z-API Instance ID" fullWidth value={form.atendabot_instance_id} onChange={handleChange('atendabot_instance_id')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Z-API Instance Token" fullWidth value={form.atendabot_instance_token} onChange={handleChange('atendabot_instance_token')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Frase Transbordo" fullWidth value={form.atendabot_transbordo_frase} onChange={handleChange('atendabot_transbordo_frase')} sx={fieldSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Telefone Transbordo" fullWidth value={form.atendabot_transbordo_phone} onChange={handleChange('atendabot_transbordo_phone')} sx={fieldSx} /></Grid>
              <Grid item xs={12}><TextField label="AI" fullWidth value={form.atendabot_ai} onChange={handleChange('atendabot_ai')} sx={fieldSx} /></Grid>
              <Grid item xs={6}><TextField label="Gemini Key" fullWidth value={form.gemini_key} onChange={handleChange('gemini_key')} sx={fieldSx} /></Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={!!form.atendabot_usar_prompt_manager}
                      onChange={handleCheck('atendabot_usar_prompt_manager')}
                    />
                  )}
                  label="Usar este prompt e não exibir a tela de Prompt para o cliente no Atendabot"
                />
                <Typography variant="body2" sx={{ color: '#64748b', ml: 4, mt: -0.5 }}>
                  Quando ativo, o Atendabot usa o prompt abaixo e a tela de Prompt fica oculta para o cliente.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Gemini Prompt"
                  fullWidth
                  multiline
                  rows={20}
                  required={!!form.atendabot_usar_prompt_manager}
                  value={form.gemini_prompt}
                  onChange={handleChange('gemini_prompt')}
                  helperText={form.atendabot_usar_prompt_manager
                    ? 'Este é o prompt em uso. Para personalizar a resposta quando pedidos estiverem desativados, inclua uma linha no formato: RESPOSTA_PEDIDOS_DESATIVADOS: sua mensagem.'
                    : 'Prompt alternativo mantido pelo Manager.'}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Prompt Gerado (tela de Prompt do Atendabot)"
                  fullWidth
                  multiline
                  rows={20}
                  value={form.generated_prompt || ''}
                  helperText={
                    form.atendabot_usar_prompt_manager
                      ? 'Somente leitura. Gerado a partir das respostas da tela de Prompt do cliente — não está em uso enquanto o prompt do Manager estiver ativo.'
                      : 'Somente leitura. É este texto que o Atendabot usa quando o prompt do Manager está desativado.'
                  }
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <Tooltip title={promptCopied ? 'Copiado!' : 'Copiar prompt gerado'}>
                          <span>
                            <IconButton
                              onClick={handleCopyGeneratedPrompt}
                              disabled={!form.generated_prompt}
                              edge="end"
                              aria-label={promptCopied ? 'Copiado!' : 'Copiar prompt gerado'}
                            >
                              {promptCopied ? <DoneOutlined sx={{ color: '#16a34a' }} /> : <ContentCopyOutlined />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...fieldSx,
                    '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], bgcolor: 'rgba(15,23,42,0.03)' },
                  }}
                />
              </Grid>
              <Grid item xs={4}><TextField label="Tempo antes de enviar para IA (seg x 1000)" type="number" fullWidth value={form.delay} onChange={handleChange('delay')} sx={fieldSx} /></Grid>
              <Grid item xs={8}><TextField label="Primeira Mensagem (automática)" fullWidth value={form.primeira_mensagem} onChange={handleChange('primeira_mensagem')} sx={fieldSx} /></Grid>
              <Grid item xs={4}><TextField label="Tempo para enviar primeira mensagem novamente" type="number" fullWidth value={form.delay_primeira_mensagem} onChange={handleChange('delay_primeira_mensagem')} sx={fieldSx} /></Grid>
              <Grid item xs={4}><TextField label="Retentativas em caso de erro" type="number" fullWidth value={form.retentativas} onChange={handleChange('retentativas')} sx={fieldSx} /></Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={!!form.usar_cardapio_onemenu}
                      onChange={handleCheck('usar_cardapio_onemenu')}
                    />
                  )}
                  label="Usar cardápio do OneMenu"
                />
                <Typography variant="body2" sx={{ color: '#64748b', ml: 4, mt: -0.5 }}>
                  Esta é a mesma configuração exibida na tela de Prompt do Atendabot.
                </Typography>
              </Grid>
            </SectionCard>

            {/* Integrações */}
            <SectionCard
              title="Integrações"
              description="Configurações de integrações externas."
              icon={<IntegrationInstructionsOutlined sx={{ fontSize: 18 }} />}
              iconSx={ICON_SLATE}
            >
              <Grid item xs={6}><FormControlLabel control={<Checkbox checked={!!form.integration_sischef_active} onChange={handleCheck('integration_sischef_active')} />} label="Sischef Ativo" /></Grid>
              <Grid item xs={6}><TextField label="Sischef Token" fullWidth value={form.integration_sischef_token} onChange={handleChange('integration_sischef_token')} sx={fieldSx} /></Grid>
            </SectionCard>

          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default RestauranteFormPage;